import { useEffect, useRef, useState } from "react";
import { useFetchBlobUrl } from "@/hooks/useFetchBlobUrl";
import { useDirectFileDownload } from "@/hooks/useDirectFileDownload";
import { Button, Image, Skeleton } from "antd";
import { DownloadOutlined, FileImageOutlined } from "@ant-design/icons";

type ChatPlotRendererProps = {
  url: string;
};

const getImageFileName = (imageUrl: string) => {
  try {
    const pathname = new URL(imageUrl).pathname;
    return pathname.split("/").pop() || "generated-plot.png";
  } catch {
    return "generated-plot.png";
  }
};

export default function ChatPlotRenderer({ url }: ChatPlotRendererProps) {
  const revokeImageRef = useRef<(() => void) | null>(null);
  const [imageSrc, setImageSrc] = useState("");
  const { createBlobUrl, error, isLoading } = useFetchBlobUrl({
    onSuccess: (blobUrl, revoke) => {
      revokeImageRef.current?.();
      revokeImageRef.current = revoke;
      setImageSrc(blobUrl);
    },
    onError: () => {
      setImageSrc("");
    },
  });
  const { downloadFile, isDownloading } = useDirectFileDownload();

  useEffect(() => {
    if (url) {
      createBlobUrl(url);
    }

    return () => {
      revokeImageRef.current?.();
      revokeImageRef.current = null;
    };
  }, [url, createBlobUrl]);

  const handleDownload = () => {
    downloadFile({
      fileUrl: url,
      fileName: getImageFileName(url),
    });
  };

  return (
    <div className="m-2 ">
      {isLoading ? (
        <div className="max-w-[320px]">
          <Skeleton.Image active className="w-full! h-45!" />
        </div>
      ) : null}
      {!isLoading && imageSrc ? (
        <div className="rounded-md border-2 border-gray-300 p-3 w-[70%] h-[70%]">
          <div className="mb-3 flex justify-end">
            <Button
              icon={<DownloadOutlined />}
              loading={isDownloading}
              onClick={handleDownload}
            >
              Download image
            </Button>
          </div>
          <Image src={imageSrc} alt="Generated plot" preview={false} />
        </div>
      ) : null}
      {!isLoading && error ? (
        <div className="flex items-center gap-2 text-[#8C2F39]">
          <FileImageOutlined />
          <p>Unable to load the generated plot image for this message.</p>
        </div>
      ) : null}
    </div>
  );
}
